import * as React from 'react';
import { Button } from '@/components/ui/Button';

interface TransactionsSectionHeaderProps {
  canEdit: boolean;
  hasChildren: boolean;
  includeChildTransactions: boolean;
  onIncludeChildTransactionsChange: (checked: boolean) => void;
  isObligation: boolean;
  isObligationPaid: boolean;
  onMarkPaid: () => void;
  onAddTransaction: () => void;
  onAddRecurringSpends: () => void;
  /** Show Select when there are more than 2 transactions */
  canSelect?: boolean;
  selectionMode?: boolean;
  selectedCount?: number;
  onEnterSelection?: () => void;
  onExitSelection?: () => void;
}

export function TransactionsSectionHeader({
  canEdit,
  hasChildren,
  includeChildTransactions,
  onIncludeChildTransactionsChange,
  isObligation,
  isObligationPaid,
  onMarkPaid,
  onAddTransaction,
  onAddRecurringSpends,
  canSelect = false,
  selectionMode = false,
  selectedCount = 0,
  onEnterSelection,
  onExitSelection,
}: TransactionsSectionHeaderProps) {
  const [showTransactionsMenu, setShowTransactionsMenu] = React.useState(false);
  const transactionsMenuRef = React.useRef<HTMLDivElement>(null);
  const showMenu = canEdit || (canSelect && !selectionMode);

  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (transactionsMenuRef.current && !transactionsMenuRef.current.contains(event.target as Node)) {
        setShowTransactionsMenu(false);
      }
    };

    if (showTransactionsMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showTransactionsMenu]);

  return (
    <div className="flex flex-col gap-2 min-w-0 md:flex-row md:items-center md:justify-between mb-3">
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <h2 className="text-lg font-semibold text-foreground">Transactions</h2>
          {showMenu && (
            <div className="relative" ref={transactionsMenuRef}>
              <button
                type="button"
                onClick={() => setShowTransactionsMenu((prev) => !prev)}
                className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-input text-muted-foreground hover:text-foreground"
                aria-label="Transaction actions"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <circle cx="12" cy="5" r="1" />
                  <circle cx="12" cy="12" r="1" />
                  <circle cx="12" cy="19" r="1" />
                </svg>
              </button>
              {showTransactionsMenu && (
                <div className="absolute left-0 mt-2 w-44 rounded-md border border-input bg-background shadow-md z-20">
                  {canSelect && !selectionMode && onEnterSelection && (
                    <button
                      type="button"
                      onClick={() => {
                        onEnterSelection();
                        setShowTransactionsMenu(false);
                      }}
                      className="w-full px-3 py-2 text-left text-sm hover:bg-muted"
                    >
                      Select
                    </button>
                  )}
                  {canEdit && (
                    <button
                      type="button"
                      onClick={() => {
                        onAddRecurringSpends();
                        setShowTransactionsMenu(false);
                      }}
                      className="w-full px-3 py-2 text-left text-sm hover:bg-muted"
                    >
                      Add recurring spends
                    </button>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
        {selectionMode ? (
          <p className="text-xs text-muted-foreground">
            Selection mode – {selectedCount} selected
          </p>
        ) : (
          <p className="text-sm text-muted-foreground hidden md:block">
            {includeChildTransactions
              ? 'Showing transactions from this budget and all child budgets'
              : 'Showing transactions from this budget only'}
          </p>
        )}
      </div>
      <div className="flex items-center gap-2 shrink-0">
        {selectionMode ? (
          <Button size="sm" variant="outline" onClick={onExitSelection}>
            Cancel
          </Button>
        ) : (
          <>
            {hasChildren && (
              <label
                className="flex items-center gap-1.5 cursor-pointer"
                title="Include child budgets"
              >
                <input
                  type="checkbox"
                  checked={includeChildTransactions}
                  onChange={(e) => onIncludeChildTransactionsChange(e.target.checked)}
                  className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="md:hidden text-muted-foreground"
                  aria-hidden="true"
                >
                  <path d="M16 3h5v5" />
                  <path d="M8 3H3v5" />
                  <path d="M12 22v-8.3a4 4 0 0 0-1.172-2.872L3 3" />
                  <path d="m15 9 6-6" />
                  <path d="M3 3l7.828 7.828A4 4 0 0 0 12 13.7V22" />
                </svg>
                <span className="text-sm text-foreground hidden md:inline">Include child budgets</span>
                <span className="sr-only">Include child budgets</span>
              </label>
            )}
            {canEdit && (
              isObligation && !isObligationPaid ? (
                <Button size="sm" onClick={onMarkPaid}>
                  <span className="md:hidden" title="Mark paid">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      aria-hidden="true"
                    >
                      <path d="M20 6 9 17l-5-5" />
                    </svg>
                  </span>
                  <span className="hidden md:inline">Mark paid</span>
                </Button>
              ) : (
                <Button size="sm" onClick={onAddTransaction} aria-label="Add transaction">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="md:mr-1"
                  >
                    <path d="M5 12h14" />
                    <path d="M12 5v14" />
                  </svg>
                  <span className="hidden md:inline">Add</span>
                </Button>
              )
            )}
          </>
        )}
      </div>
    </div>
  );
}
